'use client';

import { AnimatePresence, motion } from 'motion/react';
import Image from 'next/image';
import { useState } from 'react';

import { REVEAL_STAGGER, TextReveal } from '@/components/elements/text-reveal';
import { cn } from '@/lib/utils';

const USE_CASES = [
  {
    label: 'Weddings',
    slug: 'weddings',
    tagline: 'One film for the whole night.',
    chip: '💍 Weddings',
    image: '/images/home/use-cases/wedding-editorial.jpg',
  },
  {
    label: 'Birthdays',
    slug: 'birthdays',
    tagline: 'Every candle, every angle.',
    chip: '🎂 Birthdays',
    image: '/images/home/use-cases/birthdays.png',
  },
  {
    label: 'Travel',
    slug: 'group-trips',
    tagline: 'The trip, told by everyone.',
    chip: '✈️ Travel',
    image: '/images/home/use-cases/travel.webp',
  },
  {
    label: 'Everyday Life',
    slug: 'everyday-life',
    tagline: 'The little things, together.',
    chip: '🌿 Everyday Life',
    image: '/images/hero/cards/beach-day.jpg',
  },
] as const;

export function UseCasesSection() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const activeIndex = hoveredIndex ?? 0;

  return (
    <section className="section-padding overflow-hidden">
      <div className="container">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: {}, visible: {} }}
          className="mb-12 md:mb-16"
        >
          <TextReveal mode="animate">
            <p className="text-text-tertiary text-[11px] font-semibold tracking-[1.4px] uppercase">
              Use cases
            </p>
          </TextReveal>
          <TextReveal mode="animate" delay={REVEAL_STAGGER} className="mt-3">
            <h2 className="font-display text-2xl tracking-tight md:text-[32px]">
              Made for every moment
            </h2>
          </TextReveal>
        </motion.div>

        <div className="grid items-start gap-12 md:grid-cols-[1fr_auto] lg:gap-20">
          {/* Editorial index */}
          <motion.div
            initial={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {USE_CASES.map((useCase, i) => {
              const isActive = activeIndex === i;
              return (
                <div
                  key={useCase.slug}
                  className="border-foreground/10 border-b py-6"
                  onMouseEnter={() => setHoveredIndex(i)}
                >
                  <motion.div
                    animate={{ x: isActive && hoveredIndex === i ? 8 : 0 }}
                    transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 30,
                    }}
                  >
                    <span
                      className={cn(
                        'font-display block text-4xl tracking-tight transition-colors duration-300 md:text-5xl',
                        isActive ? 'text-foreground' : 'text-text-tertiary',
                      )}
                    >
                      {useCase.label}
                    </span>
                    <span className="text-text-tertiary mt-2 block text-[13px]">
                      {useCase.tagline}
                    </span>
                  </motion.div>
                </div>
              );
            })}
          </motion.div>

          {/* Photo preview */}
          <motion.div
            initial={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden h-[420px] w-full max-w-[380px] overflow-hidden rounded-[19px] md:block md:h-[480px] md:w-[380px]"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <Image
                  src={USE_CASES[activeIndex].image}
                  alt={USE_CASES[activeIndex].label}
                  fill
                  sizes="(min-width: 768px) 380px, 100vw"
                  className="object-cover"
                />
              </motion.div>
            </AnimatePresence>
            <span className="bg-secondary text-secondary-foreground absolute right-3 bottom-3 rounded-full px-3.5 py-1.5 text-xs whitespace-nowrap">
              {USE_CASES[activeIndex].chip}
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
